import {
  LitElement,
  html,
  styleMap,
  unsafeSVG,
  property,
} from '@oskecare/fe-utilities/lib/LitElement/index.js';
import { bound, mixin } from '@oskecare/fe-utilities/lib/decorators';
import { isMobile } from '@oskecare/fe-utilities/lib/Mixins';
import warningImportantIcon from '@oskecare/orange-design-system/src/assets/icons/warning-important.svg';
import startoverIcon from '@oskecare/orange-design-system/src/assets/icons/startover.svg';
import style from './style.scss';
import { ERROR_MODE_HEADER, ERROR_MODE_PAGE, ERROR_MODE_WIDGET } from './constants.js';

@mixin(isMobile())
class ErrorState extends LitElement {
  /**
   * Optional heading of the widget, rendered only when specified. Supported only for `widget` and `header` modes.
   *
   * @type {?string|TemplateResult}
   */
  @property({ attribute: false })
  accessor heading = null;

  /**
   * Specifies rendered markup of the component.
   *
   * @type {('widget'|'header'|'page')}
   */
  @property({ type: String })
  accessor mode = ERROR_MODE_WIDGET;

  /**
   * Whether markup should be wrapped in layout, supported only for `page` mode.
   *
   * @type {boolean}
   */
  @property({ type: Boolean, attribute: 'has-layout' })
  accessor hasLayout = false;

  static get styles() {
    return [style];
  }

  /**
   * @param {Event} event
   */
  @bound
  handleReloadClick(event) {
    event.preventDefault();
    const newEvent = new CustomEvent('reload', {
      bubbles: true,
      composed: false,
    });
    this.dispatchEvent(newEvent);
  }

  /**
   * @returns {?TemplateResult}
   */
  renderHeading() {
    return this.heading ? html` <h2 class="h1 d-flex">${this.heading}</h2> ` : null;
  }

  /**
   * @returns {TemplateResult}
   */
  static renderWarningIcon() {
    return html`
      <div class="bar__item">
        <svg class="icon icon--xlarge" role="presentation" aria-hidden="true">
          ${unsafeSVG(warningImportantIcon)}
        </svg>
      </div>
    `;
  }

  render() {
    switch (this.mode) {
      case ERROR_MODE_WIDGET:
        return html`
          <div class="card__section card__section--fill">
            ${this.renderHeading()}
            <div class="bar">
              ${ErrorState.renderWarningIcon()}
              <div class="bar__item bar__item--fill">
                <p>
                  <strong>
                    Nepodarilo sa nám načítať informácie.<br />Skúste to, prosím, neskôr.
                  </strong>
                </p>
              </div>
            </div>
          </div>
          <div class="card__section">
            <button @click=${this.handleReloadClick} class="btn">Skúsiť znova</button>
          </div>
        `;
      case ERROR_MODE_PAGE: {
        const errorTemplate = html`
          <div class="bar align-items-top">
            ${ErrorState.renderWarningIcon()}
            <div class="bar__item bar__item--fill">
              <p>
                <strong>
                  Nepodarilo sa nám načítať informácie.<br />Skúste to, prosím, neskôr.
                </strong>
              </p>
              <button @click=${this.handleReloadClick} class="btn">Skúsiť znova</button>
            </div>
          </div>
        `;
        if (this.hasLayout) {
          return html`
            <div class="section">
              <div class="container">${errorTemplate}</div>
            </div>
          `;
        }
        return errorTemplate;
      }
      case ERROR_MODE_HEADER:
        return html`
          ${this.renderHeading()}
          <div class="bar align-items-center">
            ${ErrorState.renderWarningIcon()}
            <div class="bar__item ${this.isMobile ? 'bar__item--fill' : ''}">
              <p>
                <strong>
                  Nepodarilo sa nám načítať informácie.<br />Skúste to, prosím, neskôr.
                </strong>
              </p>
            </div>
            <div class="bar__item ${!this.isMobile ? 'bar__item--fill' : ''}">
              <button @click=${this.handleReloadClick} class="btn btn--inverse">
                <svg class="icon align-middle" role="presentation" aria-hidden="true">
                  ${unsafeSVG(startoverIcon)}
                </svg>
                <span class="hide-xs-only" style=${styleMap({ 'padding-left': '0.5rem' })}>
                  Skúsiť znova
                </span>
              </button>
            </div>
          </div>
        `;
      default:
        throw new Error(`Unsupported mode for Error state: ${this.mode}`);
    }
  }
}

if (!customElements.get('osk-error')) {
  customElements.define('osk-error', ErrorState);
}

export default ErrorState;
